import {
  Avatar,
  IndicatorLabel,
  Menu,
  Tooltip,
  TooltipContent,
  Typography,
} from "@codat/orchard-ui";
import { Client } from "api/users";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "universal-cookie";

import { paths } from "components/App/paths";
import Button from "components/Button";
import ClientTypeLabel from "components/ClientTypeLabel";

import { getInstanceType } from "./UserClientsDropdown";
import styles from "./UserClientsDropdown.module.scss";

interface ClientsListProps {
  clients: Client[];
  onClientSelect: (clientId: string) => Promise<void>;
}

const cookies = new Cookies();

const CLIENTS_TO_SHOW = 5;

const ClientsList = ({ clients, onClientSelect }: ClientsListProps) => {
  const location = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);

  const visibleClients = clients.slice(0, CLIENTS_TO_SHOW);

  const clientSelectUrl = useMemo(() => {
    return `${paths.clients.SELECT}?redirectUrl=${window.location.origin}${location.pathname}`;
  }, [location.pathname]);

  const onClientSelectPageNavigate = useCallback(() => {
    cookies.remove("signin-seen");
  }, []);

  return (
    <div>
      <div className={styles.clientsHeader}>
        <Typography variant="heading_sm" as="h3">
          Switch client
        </Typography>
        {clients.length > CLIENTS_TO_SHOW && (
          <Typography
            variant="body_sm"
            as="div"
            className={styles.clientCount}
            data-testid="client-count"
          >
            {`5 / ${clients.length}`}
          </Typography>
        )}
      </div>
      <Menu.List className={styles.clientList} data-testid="client-list">
        {visibleClients.map((c) => (
          <Tooltip
            isOpen={!c.clientLoginProviderEnabled && showTooltip}
            placement="right"
            content={
              <TooltipContent>
                <Typography variant="body_sm" as="p" noMargin>
                  Client disabled via login provider
                </Typography>
              </TooltipContent>
            }
            key={c.clientId}
          >
            <Menu.Item
              id={c.clientId}
              key={c.clientId}
              data-testid="client"
              onClick={() => !c.expired && onClientSelect(c.clientId)}
              icon={<Avatar color="interface" alt={c.clientName} />}
              label={c.clientName}
              isDisabled={!c.clientLoginProviderEnabled}
              subtext={getInstanceType(c.clientType)}
              onMouseEnter={() =>
                !c.clientLoginProviderEnabled && setShowTooltip(true)
              }
              onMouseLeave={() => setShowTooltip(false)}
              shortcut={
                <div className={styles.clientPill}>
                  {c.expired ? (
                    <IndicatorLabel label="Expired" color="negative-dark" />
                  ) : (
                    <ClientTypeLabel clientType={c.clientType} />
                  )}
                </div>
              }
              className={classNames(styles.client, {
                [styles.expired]: c.expired,
              })}
            />
          </Tooltip>
        ))}
      </Menu.List>
      {clients.length > CLIENTS_TO_SHOW && (
        <div className={styles.clientsFooter}>
          <Button
            buttonName="View All Clients"
            size="small"
            label="View all clients"
            color="interface"
            variant="secondary"
            className={styles.upgradeBtn}
            to={clientSelectUrl}
            onClick={onClientSelectPageNavigate}
          />
        </div>
      )}
    </div>
  );
};

export default ClientsList;
